import { Hct, MaterialDynamicColors, SchemeMonochrome, customColor, hexFromArgb, argbFromHex } from "@material/material-color-utilities";

/**
 * Material Design 3 colour roles for Despejo (ADR-0009): monochrome scheme
 * from a black seed (brand = black and white), plus functional custom
 * colours for status only.
 */
export const BRAND_SEED = "#000000";
/** Functional colours: used only on status chips, icons and alerts. */
export const FUNCTIONAL = { warning: "#B26A00", success: "#2E7D32" } as const;

export interface M3Roles {
  primary: string;
  onPrimary: string;
  primaryContainer: string;
  onPrimaryContainer: string;
  secondary: string;
  onSecondary: string;
  secondaryContainer: string;
  onSecondaryContainer: string;
  tertiary: string;
  onTertiary: string;
  tertiaryContainer: string;
  onTertiaryContainer: string;
  error: string;
  onError: string;
  errorContainer: string;
  onErrorContainer: string;
  warning: string;
  onWarning: string;
  warningContainer: string;
  onWarningContainer: string;
  success: string;
  onSuccess: string;
  successContainer: string;
  onSuccessContainer: string;
  background: string;
  onBackground: string;
  surface: string;
  onSurface: string;
  surfaceVariant: string;
  onSurfaceVariant: string;
  surfaceDim: string;
  surfaceBright: string;
  surfaceContainerLowest: string;
  surfaceContainerLow: string;
  surfaceContainer: string;
  surfaceContainerHigh: string;
  surfaceContainerHighest: string;
  inverseSurface: string;
  inverseOnSurface: string;
  inversePrimary: string;
  outline: string;
  outlineVariant: string;
  scrim: string;
  shadow: string;
}

const DYNAMIC: (keyof M3Roles & keyof typeof MaterialDynamicColors)[] = [
  "primary", "onPrimary", "primaryContainer", "onPrimaryContainer",
  "secondary", "onSecondary", "secondaryContainer", "onSecondaryContainer",
  "tertiary", "onTertiary", "tertiaryContainer", "onTertiaryContainer",
  "error", "onError", "errorContainer", "onErrorContainer",
  "background", "onBackground", "surface", "onSurface", "surfaceVariant", "onSurfaceVariant",
  "surfaceDim", "surfaceBright", "surfaceContainerLowest", "surfaceContainerLow", "surfaceContainer",
  "surfaceContainerHigh", "surfaceContainerHighest", "inverseSurface", "inverseOnSurface", "inversePrimary",
  "outline", "outlineVariant", "scrim", "shadow",
];

export function m3Roles(mode: "light" | "dark", seed = BRAND_SEED): M3Roles {
  const scheme = new SchemeMonochrome(Hct.fromInt(argbFromHex(seed)), mode === "dark", 0);
  const roles = {} as Record<string, string>;
  for (const name of DYNAMIC) {
    const dc = (MaterialDynamicColors as unknown as Record<string, { getArgb(s: unknown): number }>)[name];
    if (!dc) throw new Error(`missing dynamic colour ${name}`);
    roles[name] = hexFromArgb(dc.getArgb(scheme));
  }
  const src = argbFromHex(seed);
  for (const [name, value] of Object.entries(FUNCTIONAL)) {
    const g = customColor(src, { name, value: argbFromHex(value), blend: false });
    const c = mode === "dark" ? g.dark : g.light;
    const cap = name[0]!.toUpperCase() + name.slice(1);
    roles[name] = hexFromArgb(c.color);
    roles[`on${cap}`] = hexFromArgb(c.onColor);
    roles[`${name}Container`] = hexFromArgb(c.colorContainer);
    roles[`on${cap}Container`] = hexFromArgb(c.onColorContainer);
  }
  return roles as unknown as M3Roles;
}

// --------------------------------------------------------------- contrast

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
}

/** WCAG 2.x contrast ratio between two #rrggbb colours. */
export function contrastRatio(a: string, b: string): number {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}
